import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../contexts/AuthContext";
import InputField from "../components/InputField";
import Button from "../components/Button";

const AccountSettings = () => {
    const { isAuthenticated, userData } = useContext(AuthContext);
    const [email, setEmail] = useState("");
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [subscriptionPlan, setSubscriptionPlan] = useState("No Plan");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    // Initialize user details from AuthContext and localStorage
    useEffect(() => {
        if (isAuthenticated) {
            // Get email from AuthContext userData first, then fallback to localStorage
            if (userData?.email) {
                setEmail(userData.email);
            } else {
                const storedUserData = localStorage.getItem("user_data") || sessionStorage.getItem("user_data");
                if (storedUserData) {
                    try {
                        const parsedUserData = JSON.parse(storedUserData);
                        setEmail(parsedUserData.email || "");
                    } catch (parseError) {
                        console.error("Error parsing stored user data:", parseError);
                    }
                }
            }

            // Get plan status from localStorage
            const planStatus = localStorage.getItem("plan_status") || sessionStorage.getItem("plan_status");
            if (planStatus) {
                try {
                    const planData = JSON.parse(planStatus);
                    setSubscriptionPlan(planData.plan || "No Plan");
                } catch (parseError) {
                    console.error("Error parsing plan status:", parseError);
                    setSubscriptionPlan("No Plan");
                }
            } else {
                setSubscriptionPlan("No Plan");
            }
        }
    }, [isAuthenticated, userData]);

    // Handle email update
    const handleEmailUpdate = async () => {
        setLoading(true);
        setMessage("");
        try {
            const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/update-email`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ email }),
            });

            if (!response.ok) {
                // Handle HTML error responses gracefully
                const contentType = response.headers.get("content-type");
                if (contentType && contentType.indexOf("application/json") !== -1) {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || "Failed to update email.");
                } else {
                    // Likely an HTML error page (404, 500, etc.)
                    throw new Error(`Email update not available (${response.status})`);
                }
            }
            
            setMessage("Email updated successfully!");
        } catch (error) {
            setMessage(error.message);
            console.error("Email update error:", error);
        } finally {
            setLoading(false);
        }
    };

    // Handle password update
    const handlePasswordUpdate = async () => {
        if (!currentPassword || !newPassword || newPassword !== confirmPassword) {
            setMessage("Passwords do not match or are empty.");
            return;
        }

        setLoading(true);
        setMessage("");
        try {
            const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/update-password`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
            });

            if (!response.ok) {
                // Handle HTML error responses gracefully
                const contentType = response.headers.get("content-type");
                if (contentType && contentType.indexOf("application/json") !== -1) {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || "Failed to update password.");
                } else {
                    // Likely an HTML error page (404, 500, etc.)
                    throw new Error(`Password update not available (${response.status})`);
                }
            }
            
            setMessage("Password updated successfully!");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (error) {
            setMessage(error.message);
            console.error("Password update error:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-8 max-w-2xl mx-auto w-[50%] font-['Archivo'] font-light">
            <h2 className="text-3xl font-bold">Account Settings</h2>

            {message && <p className="mt-4 text-center text-green-600">{message}</p>}

            <h3 className="mt-6 font-semibold">Profile Information</h3>
            <InputField
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
            />
            <Button text={loading ? "Updating..." : "Update Email"} onClick={handleEmailUpdate} />

            <h3 className="mt-6 font-semibold">Password Management</h3>
            <InputField
                label="Current Password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
            />
            <InputField
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
            />
            <InputField
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <Button text={loading ? "Updating..." : "Update Password"} onClick={handlePasswordUpdate} />

            <h3 className="mt-6 font-semibold">Subscription Information</h3>
            <p className="text-gray-600">You are currently on the <b>{subscriptionPlan}</b> plan.</p>
            <Button text="Change Subscription Plan" />

        </div>
    );
};

export default AccountSettings;
